import { useCallback, useMemo } from "react";
import { useFeatureFlagPayload } from "posthog-js/react";

import { Medication } from "@types-runway/condition";
import {
  computeShowSeeOptions as computeShow,
  filterPromotedMedications as filterMedications,
  isValidConfig,
} from "@utils/cart/upsellConfigLogic";

export interface UpsellConfigResult {
  filterPromotedMedications: (conditionName: string, medications: Medication[]) => Medication[];
  computeShowSeeOptions: (
    conditionName: string,
    medications: Medication[],
    isCartPreselected?: boolean,
    suggestedMedicationName?: string,
    suggestedMedicationId?: string
  ) => boolean;
  isActive: boolean;
}

const flagKey = "medication-upsell-config";

const useUpsellConfig = (): UpsellConfigResult => {
  const rawPayload = useFeatureFlagPayload(flagKey);
  const config = useMemo(
    () => (isValidConfig(rawPayload) ? rawPayload : null),
    [rawPayload]
  );
  const isActive = config !== null;

  const filterPromotedMedications = useCallback(
    (conditionName: string, medications: Medication[]) =>
      filterMedications(config, conditionName, medications),
    [config]
  );

  const computeShowSeeOptions = useCallback(
    (
      conditionName: string,
      medications: Medication[],
      isCartPreselected?: boolean,
      suggestedMedicationName?: string,
      suggestedMedicationId?: string
    ) => computeShow(config, conditionName, medications, isCartPreselected, suggestedMedicationName, suggestedMedicationId),
    [config]
  );

  return {
    filterPromotedMedications,
    computeShowSeeOptions,
    isActive,
  };
};

export default useUpsellConfig;
